import { femaleQuestionMedium, maleQuestionMedium } from '@/assets/icons';
import {
  HAVING_NO_PROBLEMS,
  HAVING_NO_PROBLEMS_SOURCE,
  WASTE,
  WASTE_SOURCE,
  WITHOUT_DATA,
  WITHOUT_DATA_SOURCE,
} from './quotes';

const WHAT_IS_A_PB = `Un problema es una <strong>diferencia</strong> entre la situación <strong>actual</strong> y la situación <strong>ideal</strong>.`;

const SPOT_PB = `
<div class="ia-sensei-quote">
  <span class="quote">"${HAVING_NO_PROBLEMS}"</span>
  <br>
  <span class="author">${HAVING_NO_PROBLEMS_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  ¿Qué es un problema?
</h2>
<p>
  Volvamos un momento a lo esencial:<br>
  @:sensei.WHAT_IS_A_PB<br>
  La situación ideal puede ser un estándar o un objetivo de indicador, por ejemplo.
  Así, un problema brinda una oportunidad para mejorar la situación actual y lograr que algo resulte más eficaz.<br>
  El seguimiento diario de los indicadores de rendimiento (o KPI) es un excelente punto de partida para detectar problemas.
  Un KPI que supera el objetivo es sinónimo de problema, ya que existe una diferencia manifiesta entre la línea de objetivo y los valores que la superan.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Bueno, pero que no haya problemas significaría tener la mejor situación posible, ¿no?</span>
</div>
<p>
  O que la gente debería estar más atenta. Mire a su alrededor y plantéese estas preguntas: <i>"¿Están cada cosa, equipo y máquina en su estado ideal?"</i>, <i>"¿Podría mejorar algo en mi rutina de trabajo?"</i><br>
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">¿Y si creo que hay algo que podría mejorar pero considero que no me corresponde a mí señalarlo?</span>
</div>
<p>
  La respuesta es sencilla: Utilice <strong>datos cifrados</strong>. Al notificar un problema en Shizen,
  tiene la posibilidad de rellenar varios campos. De hecho, el campo "Cuánto" está ahí para recordar que siempre hay que utilizar datos precisos para definir un problema.<br>
  Recordemos que un problema es una diferencia entre la situación actual y una situación ideal: Esto quiere decir que
  cualquier persona que lea la descripción de un problema debe ser capaz de entender cuál es la diferencia exacta entre la situación ideal y la actual.<br>
  Para ello, hacen falta datos.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  Tipos de despilfarro
</h2>
<p>
  Entender los principales tipos de despilfarro puede ayudarle a identificar los problemas.<br>
  Denominamos a estos despilfarros "Muda", que se dividen en 7 tipos principales:
  <ul>
    <li>
      Sobreproducción: Esto es, producir una cantidad excesiva o en un momento inadecuado. Esto entraña inevitablemente una cantidad considerable de existencias y el consiguiente despilfarro de espacio.
    </li>
    <li>
      Espera: Se produce, por ejemplo, cada vez que un empleado tiene que esperar al resultado de una máquina. Podemos entonces suponer que se puede optimizar el \"flujo\", es decir, que podemos replantearnos la secuencia de los pasos del proceso.
    </li>
    <li>
      Transporte: Un Muda de transporte tiene lugar cuando hay zonas relacionadas demasiado alejadas. Por ejemplo, el área de producción A produce un conjunto de piezas que enviar a un área de producción B. Ambas se encuentran tan alejadas que es preciso conducir carretillas de una a otra.
    </li>
    <li>
      Proceso: Se da cuando la producción de un equipo o de un área de producción no resulta de utilidad al cliente. Por ejemplo, una pieza que se produce con una dimensión errónea, lo que obliga al cliente a desecharla o a reprocesarla. Nos encontraremos con este tipo de problema cuando no hayamos comprendido (o descrito) bien cuál es la necesidad del cliente. Deberemos identificar con exactitud cuáles son las necesidades del cliente para determinar cuáles serán las etapas del proceso de producción.
    </li>
    <li>
      Inventario: Suele deberse a un fenómeno de sobreproducción. Implica que tengamos existencias de objetos inutilizables (productos que se estropean o han caducado). Podría ser necesario revisar el suministro para evitar tener una cantidad demasiado grande y, por tanto, difícil de gestionar. Si el inventario es necesario, como en el caso de un almacén de logística, tal vez sea el sistema de almacenamiento el que precise mejora.
    </li>
    <li>
      Movimiento: No hay que confundirlo con el transporte. En este caso, el despilfarro implica que una persona (o máquina) debe hacer movimientos difíciles para alcanzar herramientas, materiales etc.
    </li>
    <li>
      Defectos: Se trata de un problema de calidad corriente que surge debido a un proceso de producción mal controlado o a no haber transmitido correctamente las indicaciones.
    </li>
  </ul>
  A veces puede añadirse un tipo adicional de Muda. Sin embargo, para empezar, por lo general es posible centrarse en estos 7 tipos.
  <div class="ia-sensei-quote">
    <span class="quote">"${WASTE}"</span>
    <br>
    <span class="author">${WASTE_SOURCE}</span>
  </div>
</p>
`;

const SPOT_PB_TITLE = 'Detectar un problema';

export default {
  SPOT_PB,
  SPOT_PB_TITLE,
  WHAT_IS_A_PB,
};
